// File: client/src/components/rules/expressionEditor/EJSUtils.js

/**
 * A simple EJS-like template engine for evaluating templates
 * This is a minimal implementation for client-side use in the workflow editor
 *
 * @param {string} template - The EJS template string
 * @param {Object} data - The data object to use for evaluation
 * @returns {string} - The evaluated template
 */
export const evaluateEjsTemplate = (template, data) => {
    if (!template || typeof template !== 'string') {
        return '';
    }

    // Process <%= expression %> tags (output with escaping)
    let result = template.replace(/<%=\s*([\s\S]+?)\s*%>/g, (match, p1) => {
        try {
            const func = new Function('record', 'workflow', `return ${p1};`);
            const value = func(data, { id: 'test-workflow' });

            if (value === undefined) {
                return 'undefined';
            }
            if (value === null) {
                return 'null';
            }

            return String(value).replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        } catch (error) {
            throw new Error(`Error evaluating expression "${p1}": ${error.message}`);
        }
    });

    // Process <%- expression %> tags (output without escaping)
    result = result.replace(/<%-\s*([\s\S]+?)\s*%>/g, (match, p1) => {
        try {
            const func = new Function('record', 'workflow', `return ${p1};`);
            const value = func(data, { id: 'test-workflow' });

            if (value === undefined) {
                return 'undefined';
            }
            if (value === null) {
                return 'null';
            }

            return String(value);
        } catch (error) {
            throw new Error(`Error evaluating expression "${p1}": ${error.message}`);
        }
    });

    // Process <% scriptlet %> tags (control flow)
    // This is a simplified implementation that doesn't support full EJS control flow
    // In a real implementation, you'd use a proper EJS engine
    const scriptParts = [];
    let lastIndex = 0;
    const scriptletRegex = /<%(?!\=|-)([\s\S]+?)%>/g;
    let match;

    while ((match = scriptletRegex.exec(result)) !== null) {
        // Add the text before the scriptlet
        scriptParts.push(JSON.stringify(result.substring(lastIndex, match.index)));

        // Add the scriptlet code
        scriptParts.push(`(() => { ${match[1]}; return ''; })()`);

        lastIndex = match.index + match[0].length;
    }

    // Add the text after the last scriptlet
    scriptParts.push(JSON.stringify(result.substring(lastIndex)));

    // Join all parts with + operator
    const scriptSource = scriptParts.join(' + ');

    try {
        // Create a function that executes the script
        const scriptFunc = new Function('record', 'workflow', `return ${scriptSource};`);
        result = scriptFunc(data, { id: 'test-workflow' });
    } catch (error) {
        throw new Error(`Error evaluating scriptlet: ${error.message}`);
    }

    return result;
};

/**
 * Get language suggestions for EJS template auto-completion
 * @returns {Object} An object with EJS language features for Monaco editor
 */
export const getEjsLanguageFeatures = () => {
    return {
        // EJS tag completions
        tagCompletions: [
            {
                label: '<%=  %>',
                detail: 'Output with HTML escaping',
                insertText: '<%= $1 %>$0',
                documentation: 'Outputs the result of a JavaScript expression with HTML escaping'
            },
            {
                label: '<%-  %>',
                detail: 'Output without HTML escaping',
                insertText: '<%- $1 %>$0',
                documentation: 'Outputs the result of a JavaScript expression without HTML escaping'
            },
            {
                label: '<%  %>',
                detail: 'JavaScript code',
                insertText: '<% $1 %>$0',
                documentation: 'Executes JavaScript code without output'
            },
            {
                label: '<% if () { %>  <% } %>',
                detail: 'If condition',
                insertText: '<% if ($1) { %>\n\t$2\n<% } %>$0',
                documentation: 'Conditional if statement'
            },
            {
                label: '<% if () { %>  <% } else { %>  <% } %>',
                detail: 'If-else condition',
                insertText: '<% if ($1) { %>\n\t$2\n<% } else { %>\n\t$3\n<% } %>$0',
                documentation: 'Conditional if-else statement'
            },
            {
                label: '<% for () { %>  <% } %>',
                detail: 'For loop',
                insertText: '<% for (let $1 = 0; $1 < $2.length; $1++) { %>\n\t$3\n<% } %>$0',
                documentation: 'Loop with for statement'
            },
            {
                label: '<% forEach () { %>  <% } %>',
                detail: 'ForEach loop',
                insertText: '<% $1.forEach(function($2) { %>\n\t$3\n<% }); %>$0',
                documentation: 'Loop with forEach statement'
            },
            {
                label: '<% include() %>',
                detail: 'Include partial',
                insertText: '<% include($1) %>$0',
                documentation: 'Include another template (partial)'
            }
        ]
    };
};

/**
 * Determine if cursor is inside an EJS expression
 * @param {string} text - The text to check
 * @param {number} position - The cursor position
 * @returns {Object} Information about the EJS expression context
 */
export const getEjsExpressionContext = (text, position) => {
    if (!text || position < 0 || position > text.length) {
        return { insideExpression: false };
    }

    const textBeforeCursor = text.substring(0, position);
    const textAfterCursor = text.substring(position);

    // Find the last opening tags before cursor
    const lastOpenEqualsPos = textBeforeCursor.lastIndexOf('<%=');
    const lastOpenHyphenPos = textBeforeCursor.lastIndexOf('<%-');
    const lastOpenScriptletPos = textBeforeCursor.lastIndexOf('<%');

    // Find the last closing tag before cursor
    const lastClosePos = textBeforeCursor.lastIndexOf('%>');

    // Get the position of the most recent opening tag
    const lastOpenTagWithExpressionPos = Math.max(lastOpenEqualsPos, lastOpenHyphenPos);
    const mostRecentOpenTag = Math.max(lastOpenTagWithExpressionPos, lastOpenScriptletPos);

    // Check if we're inside an expression
    if (mostRecentOpenTag > lastClosePos) {
        const isExpression = lastOpenEqualsPos === mostRecentOpenTag || lastOpenHyphenPos === mostRecentOpenTag;
        const isScriptlet = lastOpenScriptletPos === mostRecentOpenTag &&
            lastOpenScriptletPos !== lastOpenEqualsPos &&
            lastOpenScriptletPos !== lastOpenHyphenPos;

        // Find the next closing tag
        const nextClosePos = textAfterCursor.indexOf('%>');
        if (nextClosePos === -1) return { insideExpression: false }; // No closing tag found

        return {
            insideExpression: true,
            isOutputTag: isExpression,
            isScriptletTag: isScriptlet,
            expressionStart: mostRecentOpenTag + (isExpression ? 3 : 2), // Skip <%= or <%
            expressionEnd: position + nextClosePos
        };
    }

    return { insideExpression: false };
};
